import clsx from "clsx";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { osInfo } from "./os.js";

/**
 * The logo of a server's or image's operating system.
 *
 * - `mono`: in the text colour, for compact places (lists, tabs);
 * - `brand`: in the brand colour, lightened in the dark theme (`.os-brand`);
 * - `tile`: white on a rounded tile of the brand colour (cards).
 *
 * It has an accessible name ("Ubuntu 26.04") unless `decorative`, for
 * places that print the name next to it.
 */
export function OsIcon({
  flavor,
  version = null,
  description = null,
  variant = "mono",
  decorative = false,
  className,
}: {
  flavor: string | null | undefined;
  version?: string | null | undefined;
  /** The image description: names the system of snapshots whose flavour is unknown. */
  description?: string | null | undefined;
  variant?: "mono" | "brand" | "tile" | undefined;
  decorative?: boolean | undefined;
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  const os = osInfo(flavor, description);
  const name = [os.name || t("common.unknownOs"), version].filter(Boolean).join(" ");
  const label = decorative ? { "aria-hidden": true } : { role: "img", "aria-label": name };
  const glyph = (
    <svg
      viewBox="0 0 24 24"
      className={clsx(
        variant === "tile" ? "size-[60%]" : (className ?? "size-4"),
        "shrink-0",
        variant === "brand" && "os-brand",
      )}
      fill={variant === "tile" ? "#fff" : variant === "mono" ? "currentColor" : undefined}
      style={variant === "brand" ? ({ "--os-brand": os.color } as CSSProperties) : undefined}
      data-os={os.key}
      {...(variant === "tile" ? { "aria-hidden": true } : label)}
    >
      {!decorative && variant !== "tile" && <title>{name}</title>}
      <path d={os.path} fillRule={os.evenOdd ? "evenodd" : undefined} />
    </svg>
  );
  if (variant !== "tile") return glyph;
  return (
    <span
      className={clsx(
        "flex shrink-0 items-center justify-center rounded-lg",
        className ?? "size-9",
      )}
      style={{ background: os.color }}
      {...label}
      title={decorative ? undefined : name}
    >
      {glyph}
    </span>
  );
}
